import type { Role, RoleScope } from "../state/role";
import type RoleId from "../state/RoleId";
import type { SelectOption } from "../types/Menu";
import type { TFunction } from "i18next";
import { keyFromSelector } from "i18next";
import { isNil } from "lodash-es";
import type { Grant } from "../state/grant";
import type CustomerId from "../state/CustomerId";
import type { TranslationKey } from "../state/i18n";
import type { Group } from "../state/group";
import { PERMISSION_DEFINITIONS_BY_TYPE, type PermissionType } from "../state/Permission";

const hasPermissionInScope = (role: Role, scope: RoleScope) =>
  role.permissions.some((permission) => PERMISSION_DEFINITIONS_BY_TYPE[permission]?.scope === scope);

export const getUniqueRoleNames = (
  grants: Grant[],
  customerId: CustomerId,
  rolesById: Record<string, Role>,
  t: TFunction,
): string[] => [
  ...new Set(
    grants
      .filter((grant) => grant.customerId.equals(customerId) && grant.resourceType === "CUSTOMER")
      .map((grant) => rolesById[grant.roleId.toString()])
      .filter((role) => !isNil(role))
      .map((role) => getRoleName(role, t)),
  ),
];

export const getRoleName = (role: Role, t: TFunction): string => (role.system ? t(role.name) : role.name);
export const getRoleDescription = (role: Role, t: TFunction): string | undefined => {
  if (isNil(role.description) || role.description === "") {
    return "-";
  }
  if (role.system) {
    return t(role.description);
  }
  return role.description;
};

export const orderRolesByName = (roles: Role[], t: TFunction) =>
  [...roles].sort((a, b) => {
    if (a.system && a.type === "USER") {
      return -1;
    }
    if (b.system && b.type === "USER") {
      return 1;
    }
    return getRoleName(a, t).localeCompare(getRoleName(b, t));
  });

export const toGroupedRoleOptions = (roles: Role[], t: TFunction, customGroupLabel: string): SelectOption<RoleId>[] => {
  const toOption = (role: Role, group?: string): SelectOption<RoleId> => ({
    variant: "text",
    label: getRoleName(role, t),
    value: role.id,
    description: getRoleDescription(role, t),
    ...(isNil(group) ? {} : { group }),
  });
  return [
    ...orderRolesByName(filterSystemRoles(roles), t).map((role) => toOption(role)),
    ...orderRolesByName(filterCustomRoles(roles), t).map((role) => toOption(role, customGroupLabel)),
  ];
};

// Account-level roles are CUSTOMER-scoped, plus MIXED roles that actually carry a CUSTOMER-scoped
// permission — a MIXED role with only form/folder permissions grants nothing at the account level.
export const filterAccountRoles = (roles: Role[]) =>
  roles.filter(
    (role) => role.scope === "CUSTOMER" || (role.scope === "MIXED" && hasPermissionInScope(role, "CUSTOMER")),
  );

export const filterFormRoles = (roles: Role[]) => roles.filter((role) => role.scope === "FORM");

export const filterFolderRoles = (roles: Role[]) => roles.filter((role) => role.scope === "FOLDER");

export const filterSystemRoles = (roles: Role[]) => roles.filter((role) => role.system);

export const filterCustomRoles = (roles: Role[]) => roles.filter((role) => !role.system);

export const removeAccountOwner = (roles: Role[]) =>
  roles.filter((role) => !(role.system && role.type === "ACCOUNT_OWNER"));

export const filterMixedRoles = (roles: Role[]) => roles.filter((role) => role.scope === "MIXED");

export const filterMixedRolesForScope = (roles: Role[], scope: RoleScope) =>
  filterMixedRoles(roles).filter((role) => hasPermissionInScope(role, scope));

export type GroupedRoles = {
  formRoles: Role[];
  folderRoles: Role[];
  accountSystemRoles: Role[];
  accountCustomRoles: Role[];
};

export const groupRoles = (roles: Role[]): GroupedRoles => ({
  formRoles: filterFormRoles(roles),
  folderRoles: filterFolderRoles(roles),
  accountSystemRoles: filterAccountRoles(filterSystemRoles(roles)),
  accountCustomRoles: filterAccountRoles(filterCustomRoles(roles)),
});

export const getAllRoles = (groupedRoles: GroupedRoles) => [
  ...groupedRoles.formRoles,
  ...groupedRoles.folderRoles,
  ...groupedRoles.accountCustomRoles,
  ...groupedRoles.accountSystemRoles,
];

export const toRoleByIdMap = (roles: Role[]): Map<string, Role> =>
  new Map(roles.map((role) => [role.id.toString(), role]));

export type PermissionInfo = { title: string; description: string; groups: Group[] };

const toPermissionInfo = (permission: PermissionType, t: TFunction): PermissionInfo | undefined => {
  const def = PERMISSION_DEFINITIONS_BY_TYPE[permission];
  return isNil(def) ? undefined : { title: t(def.name), description: t(def.description), groups: [] };
};

export const toPermissionInfos = (entries: Iterable<[PermissionType, Group[]]>, t: TFunction): PermissionInfo[] =>
  [...entries]
    .map(([permission, groups]) => {
      const info = toPermissionInfo(permission, t);
      return isNil(info) ? undefined : { ...info, groups };
    })
    .filter((info): info is PermissionInfo => !isNil(info));

export const getGroupCustomerPermissions = (group: Group, roleById: Map<string, Role>): PermissionType[] =>
  group.grants
    .filter((grant) => grant.resourceType === "CUSTOMER")
    .flatMap((grant) => roleById.get(grant.roleId.toString())?.permissions ?? []);

export const canManageAllForms = (grants: Grant[], customerId: CustomerId, roleById: Map<string, Role>): boolean =>
  grants.some(
    (grant) =>
      grant.customerId.equals(customerId) &&
      grant.resourceType === "CUSTOMER" &&
      (roleById.get(grant.roleId.toString())?.permissions.includes("MANAGE_FORMS") ?? false),
  );

export const getCustomerRole = (grants: Grant[], customerId: CustomerId, roles: Role[]): Role | undefined => {
  const customerGrant = grants.find(
    (grant) => grant.customerId.equals(customerId) && grant.resourceType === "CUSTOMER",
  );
  if (isNil(customerGrant)) {
    return;
  }
  return roles.find((role) => role.id.equals(customerGrant.roleId));
};

export const getAccountRole = (grants: Grant[], groupedRoles: GroupedRoles | undefined) =>
  [...(groupedRoles?.accountSystemRoles ?? []), ...(groupedRoles?.accountCustomRoles ?? [])].find((role) =>
    grants.some((grant) => grant.resourceType === "CUSTOMER" && grant.roleId.equals(role.id)),
  );

export const ROLE_SCOPE_TRANSLATION_KEYS: Record<RoleScope, TranslationKey> = {
  CUSTOMER: keyFromSelector(($) => $.ROLE_SCOPE_CUSTOMER),
  FOLDER: keyFromSelector(($) => $.ROLE_SCOPE_FOLDER),
  FORM: keyFromSelector(($) => $.ROLE_SCOPE_FORM),
  MIXED: keyFromSelector(($) => $.ROLE_SCOPE_MIXED),
};

export const isAccountAdministrator = (role?: Role) =>
  !isNil(role) && role.system && role.type === "ACCOUNT_ADMINISTRATOR";

export const resolveGrantOperation = (
  roleId: RoleId | null,
  currentRoleId: RoleId | null,
): "ADD" | "REMOVE" | "UPDATE" | null => {
  if (isNil(roleId)) {
    return isNil(currentRoleId) ? null : "REMOVE";
  }
  if (isNil(currentRoleId)) {
    return "ADD";
  }
  return roleId.equals(currentRoleId) ? null : "UPDATE";
};
